import type { MouseEventHandler } from 'react';

import { Field, FieldDescription, FieldError } from '@/components/design-system/field';
import { Input } from '@/components/design-system/input';
import InputWithIcon from '@/components/ui/InputWithIcon';
import type { TranslationKey } from '@/i18n/i18n';
import { cn } from '@/utils/cn';
import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormFieldLabel } from './FormFieldLabel';

type IconComponent = React.ComponentType<{ className?: string }>;

interface FormInputProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  label?: TranslationKey;
  placeholder?: TranslationKey;
  description?: TranslationKey;
  tooltip?: TranslationKey;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  inputClassName?: string;
  autoComplete?: 'username' | 'email' | 'new-email' | 'off';
  isRequired?: boolean;
  showOptional?: boolean;
  leftIcon?: IconComponent;
  rightIcon?: IconComponent;
  onRightIconClick?: MouseEventHandler<HTMLButtonElement> | (() => void);
  status?: 'default' | 'error' | 'success' | 'warning';
  hideErrorMessage?: boolean;
}

export const FormInput = <TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  label,
  placeholder,
  description,
  tooltip,
  disabled,
  readOnly,
  className,
  inputClassName,
  autoComplete = 'off',
  isRequired = false,
  leftIcon,
  rightIcon,
  onRightIconClick,
  status,
  hideErrorMessage = false
}: FormInputProps<TFieldValues>) => {
  const { t } = useTranslation();

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <Field data-invalid={fieldState.invalid} className={cn(className)} data-testid={`form-field-${name}`}>
          {label && (
            <FormFieldLabel
              label={label}
              tooltip={tooltip}
              isRequired={isRequired}
              fieldError={fieldState.error}
              className="font-normal"
            />
          )}
          {leftIcon || rightIcon ? (
            <InputWithIcon
              {...field}
              status={fieldState.error ? 'error' : (status ?? 'default')}
              autoComplete={autoComplete}
              placeholder={placeholder ? t(placeholder) : undefined}
              disabled={disabled}
              leftIcon={leftIcon}
              rightIcon={rightIcon}
              onRightIconClick={onRightIconClick}
              className="w-full"
              aria-invalid={fieldState.invalid}
              data-testid={`form-input-${name}`}
            />
          ) : (
            <Input
              {...field}
              status={fieldState.error ? 'error' : (status ?? 'default')}
              autoComplete={autoComplete}
              placeholder={placeholder ? t(placeholder) : undefined}
              disabled={disabled}
              readOnly={readOnly}
              aria-invalid={fieldState.invalid}
              data-testid={`form-input-${name}`}
              className={inputClassName}
            />
          )}
          {description && <FieldDescription className="text-sm text-neutral-500">{t(description)}</FieldDescription>}
          {!hideErrorMessage && <FieldError errors={fieldState.error ? [fieldState.error] : undefined} />}
        </Field>
      )}
    />
  );
};
